// Veri Girişi grid'inin tablo gövdesi: colgroup + başlık + (sanal pencereli) satır listesi.
// KpiGrid.tsx'ten salt taşıma (600 satır sınırı); durum/olaylar KpiGrid'de kalır, burada yalnız render.

import type { ComponentProps } from "react";
import {
  gridColumnWidths,
  type totalKindOf,
  type CellPos,
  type GridColumn,
  type GridRow,
} from "../../lib/kpi-grid-nav";
import type { PartialLookup } from "../../lib/kpi-partial-info";
import { KpiGridHead } from "./KpiGridHead";
import { KpiGridRow } from "./KpiGridRow";
import type { useFormulaHover } from "./use-formula-hover";
import type { VirtualRowsApi } from "./use-virtual-rows";

type RowProps = ComponentProps<typeof KpiGridRow>;

export interface KpiGridBodyProps {
  rows: GridRow[];
  visibleRows: GridRow[];
  columns: GridColumn[];
  win: VirtualRowsApi["window"];
  theadRef: ComponentProps<typeof KpiGridHead>["theadRef"];
  totalKind: ReturnType<typeof totalKindOf>;
  fiscalStartMonth: number;
  narrow: boolean;
  nameColWidth: number;
  showChangeCol: boolean;
  statusCol: boolean;
  stickyStatus: boolean;
  totalCols: number;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onPaste: React.ClipboardEventHandler;
  hoverHandlers: ReturnType<typeof useFormulaHover>["handlers"];
  focus: CellPos | null;
  flashId: string | null;
  readOnly: boolean;
  saveErrors: Record<string, string>;
  cellRefs: RowProps["cellRefs"];
  onToggleBand: RowProps["onToggleBand"];
  onFocusCell: RowProps["onFocusCell"];
  onOpenFormula: (pos: CellPos) => void;
  onOpenEditor: (pos: CellPos) => void;
  onOpenEvidence: RowProps["onOpenEvidence"];
  onOpenReported: RowProps["onOpenReported"];
  formMismatchFor: RowProps["formMismatchFor"];
  onApplyFormValue: RowProps["onApplyFormValue"];
  clientView: RowProps["clientView"];
  notes: RowProps["notes"];
  onOpenNotes: RowProps["onOpenNotes"];
  partialLookup: PartialLookup | undefined;
  isRecomputing?: RowProps["isRecomputing"];
}

export function KpiGridBody(p: KpiGridBodyProps): JSX.Element {
  const {
    rows,
    visibleRows,
    columns,
    win,
    theadRef,
    totalKind,
    fiscalStartMonth,
    narrow,
    nameColWidth,
    showChangeCol,
    statusCol,
    stickyStatus,
    totalCols,
    onKeyDown,
    onPaste,
    hoverHandlers,
    focus,
    flashId,
    readOnly,
    saveErrors,
    cellRefs,
    onToggleBand,
    onFocusCell,
    onOpenFormula,
    onOpenEditor,
    onOpenEvidence,
    onOpenReported,
    formMismatchFor,
    onApplyFormValue,
    clientView,
    notes,
    onOpenNotes,
    partialLookup,
  } = p;
  return (
    <table
      role="treegrid"
      aria-label="Performans göstergeleri"
      aria-rowcount={rows.length}
      style={{
        borderCollapse: "separate",
        borderSpacing: 0,
        // Finar #5 (2026-10-30, ekran kaydı): yalnız minWidth varken sütun genişlikleri render
        // edilen (sanal) satırların içeriğinden hesaplanıyor, kaydırmada tablo 830↔1120 px arasında
        // zıplıyordu. width 100% + fixed + colgroup → genişlik kaba bağlı, pencereden bağımsız.
        width: "100%",
        tableLayout: "fixed",
        minWidth: 420 + columns.length * (narrow ? 88 : 96) + (showChangeCol ? 90 : 0),
      }}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      {...hoverHandlers}
    >
      <colgroup>
        {gridColumnWidths({ columns, nameColWidth, showChangeCol, statusCol }).map((w, i) => (
          <col key={i} {...(w !== null ? { style: { width: w } } : {})} />
        ))}
      </colgroup>
      <KpiGridHead
        theadRef={theadRef}
        columns={columns}
        totalKind={totalKind}
        fiscalStartMonth={fiscalStartMonth}
        narrow={narrow}
        nameColWidth={nameColWidth}
        showChangeCol={showChangeCol}
        statusCol={statusCol}
        stickyStatus={stickyStatus}
      />
      <tbody>
        {win.active && win.topPad > 0 ? (
          <tr role="presentation" aria-hidden="true" data-testid="kpi-grid-pad-top">
            <td colSpan={totalCols} style={{ height: win.topPad, padding: 0, border: 0 }} />
          </tr>
        ) : null}
        {visibleRows.map((r, i) => {
          const ri = win.active ? win.start + i : i;
          return (
            <KpiGridRow
              key={r.indicator.id}
              row={r}
              rowIndex={ri}
              columns={columns}
              showChangeCol={showChangeCol}
              focus={focus}
              flash={flashId === r.indicator.id}
              readOnly={readOnly}
              saveErrors={saveErrors}
              cellRefs={cellRefs}
              onToggleBand={onToggleBand}
              onFocusCell={onFocusCell}
              onActivate={(pos) => (r.indicator.isDerived ? onOpenFormula(pos) : onOpenEditor(pos))}
              onOpenEvidence={onOpenEvidence}
              onOpenReported={onOpenReported}
              formMismatchFor={formMismatchFor}
              onApplyFormValue={onApplyFormValue}
              clientView={clientView ? { ...clientView, stickyStatus, nameColWidth } : undefined}
              notes={notes}
              onOpenNotes={onOpenNotes}
              nameColWidth={nameColWidth}
              narrow={narrow}
              partialLookup={partialLookup}
              isRecomputing={p.isRecomputing}
            />
          );
        })}
        {win.active && win.bottomPad > 0 ? (
          <tr role="presentation" aria-hidden="true" data-testid="kpi-grid-pad-bottom">
            <td colSpan={totalCols} style={{ height: win.bottomPad, padding: 0, border: 0 }} />
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}
